import type { ExperimentConfig } from '@/types';

const experiment7Config: ExperimentConfig = {
  id: 'exp7',
  number: 7,
  name: 'Gravimetric Analysis',
  description: 'Determine chloride in a soluble sample by precipitation as AgCl and phosphorus in fertilizer by precipitation as magnesium ammonium phosphate using gravimetric techniques.',
  estimatedTime: '120 minutes',
  learningObjectives: [
    'Understand principles of gravimetric analysis (precipitate → filter → dry → weigh)',
    'Apply stoichiometry using gravimetric factors (GF_Cl = 35.45/143.32 = 0.2473)',
    'Perform quantitative precipitation with 0.5 M AgNO₃ and 10% MgSO₄·7H₂O',
    'Master vacuum filtration with Buchner funnel and rubber policeman',
    'Understand "constant mass" drying technique at ~100 °C',
    'Test for complete precipitation (add drop of AgNO₃ to supernatant)',
    'Calculate %Cl = (g Cl in sample / g of unknown sample) × 100',
    'Calculate %P using GF = 30.97/245.40 and %P₂O₅ using GF = 141.92/(2 × 245.40)',
    'Perform each part in duplicate',
  ],
  safetyNotes: [
    'Silver nitrate (0.5 M AgNO₃) stains skin black - wear gloves',
    '6 M HNO₃ - handle with care in fume hood',
    'Use caution with hot plates and boiling solutions - do not boil solutions',
    '2 M ammonia (NH₃) has strong odor - work in ventilated area',
    'Acetone is highly flammable - keep away from open flames',
    '75% isopropyl alcohol is flammable - keep away from heat sources',
  ],
  requiredEquipment: [
    'Balance',
    '250 mL beakers',
    'Hotplate',
    'Funnels',
    'Funnel support',
    'Plastic wash bottle',
    'Ring stand and wire gauze',
    'Stirring rods',
    'Rubber policeman',
    'Filter paper (large size & coarse)',
    'Watch glass',
    'Graduated cylinders',
    'Buchner funnel (11 cm for Part 1, 5 inch for Part 2)',
    'Vacuum flask',
    'Drying oven (set to ~100 °C)',
  ],
  parts: [
    {
      id: 0,
      name: 'summary',
      title: 'Summary',
      steps: [],
      equipment: [],
      initialState: {},
    },
    {
      id: 1,
      name: 'chloride',
      title: 'Part I: Chloride in Soluble Sample',
      equipment: [
        'Balance',
        '250 mL beaker',
        'Hotplate',
        'Buchner funnel (11 cm)',
        'Vacuum flask',
        'Filter paper (large, 11 cm, coarse)',
        'Drying oven (~100 °C)',
        'Watch glass',
        'Stirring rods',
        'Rubber policeman',
        'Plastic wash bottle',
        'Graduated cylinders'
      ],
      initialState: {
        sampleMass: null,
        precipitateMass: null,
        percentCl: null,
        balance: {
          reading: 0,
          isTared: false,
          isStable: true
        },
        precipitation: {
          isComplete: false,
          isHeating: false,
          isStirring: false,
          precipitateAmount: 0
        },
        filtration: {
          vacuumOn: false,
          precipitateCollected: 0,
          washCount: 0
        },
        drying: {
          isComplete: false,
          massReadings: []
        }
      },
      steps: [
        {
          id: 1,
          title: 'Weigh Sample',
          shortDescription: 'Weigh 0.2-0.4 g of the unknown sample on the analytical balance',
          handler: 'handleWeighSample',
          validation: {
            requires: ['balance.isTared'],
            condition: 'sampleMass >= 0.2 && sampleMass <= 0.4'
          },
          infoContent: {
            title: 'Analytical Balance Precision',
            content: 'The analytical balance measures mass to 4 decimal places (0.0001 g). This precision is essential for gravimetric analysis where small mass differences affect the final calculation.',
            whatHappens: 'The unknown chloride sample is weighed on an analytical balance to four decimal places, providing the precise initial mass needed for the gravimetric calculation.',
            observe: [
              'Tare the balance, then weigh 0.2-0.4 g of the unknown sample',
              'Record the mass to exactly four decimal places (0.0001 g)',
              'Allow the balance reading to stabilize before recording',
            ],
          }
        },
        {
          id: 2,
          title: 'Transfer to Beaker',
          shortDescription: 'Quantitatively transfer the sample to a 250 mL beaker',
          handler: 'handleTransferSample',
          validation: {
            requires: ['sampleMass']
          }
        },
        {
          id: 3,
          title: 'Dissolve Sample',
          shortDescription: 'Add 150 mL of distilled water and stir to dissolve',
          handler: 'handleDissolveSample',
          infoContent: {
            title: 'Complete Dissolution',
            content: 'The sample must be completely dissolved before precipitation. Any undissolved material will lead to inaccurate results.',
            whatHappens: 'The unknown sample is dissolved in 150 mL of distilled water to bring all chloride ions into solution for complete reaction with silver nitrate.',
            observe: [
              'Add 150 mL of distilled water and stir thoroughly',
              'Verify that no undissolved solid remains at the bottom of the beaker',
              'The solution should be clear if the sample is fully soluble',
            ],
          }
        },
        {
          id: 4,
          title: 'Acidify Solution',
          shortDescription: 'Add 1 mL of 6 M HNO₃ to acidify the solution',
          handler: 'handleAddAcid',
          infoContent: {
            title: 'Why Acidify?',
            content: 'Add 1 mL of 6 M HNO₃ to acidify the solution. Acidification prevents the precipitation of silver hydroxide and other basic silver salts that would contaminate the AgCl precipitate.',
            whatHappens: 'Nitric acid acidifies the solution to prevent co-precipitation of silver hydroxide or silver carbonate, which would contaminate the AgCl precipitate and inflate results.',
            observe: [
              'Add exactly 1 mL of 6 M HNO3 to the dissolved sample',
              'The solution remains clear after acidification',
              'Work in the fume hood when handling 6 M HNO3',
            ],
          }
        },
        {
          id: 5,
          title: 'Add Silver Nitrate',
          shortDescription: 'Slowly add ~20 mL of 0.5 M AgNO₃ solution while stirring',
          handler: 'handleAddAgNO3',
          infoContent: {
            title: 'Precipitation Reaction',
            content: 'The chloride ions react with silver ions to form white silver chloride precipitate: Ag+(aq) + Cl-(aq) → AgCl(s). The Ksp of AgCl is 1.77 × 10^-10, making it very insoluble.',
            chemicalEquation: 'Ag⁺(aq) + Cl⁻(aq) → AgCl(s)',
            whatHappens: 'Silver nitrate reacts with chloride ions to form a white, curdy precipitate of silver chloride (Ksp = 1.77 x 10^-10), which is highly insoluble.',
            observe: [
              'A white, curdy precipitate of AgCl forms immediately upon adding AgNO3',
              'Add the AgNO3 slowly (~20 mL of 0.5 M) while stirring continuously',
              'The precipitate settles to the bottom of the beaker',
            ],
          }
        },
        {
          id: 6,
          title: 'Heat and Digest',
          shortDescription: 'Warm the solution gently with a hotplate for 5-10 minutes. Do not boil.',
          handler: 'handleHeatSolution',
          infoContent: {
            title: 'Why Heat the Precipitate?',
            content: 'Heating causes small particles to coagulate into larger crystals that filter more easily. This process is called digestion or ripening.',
            whatHappens: 'Gentle heating promotes Ostwald ripening, where small AgCl particles dissolve and redeposit on larger crystals, producing coarser particles that filter more efficiently.',
            observe: [
              'Warm the solution gently on a hotplate for 5-10 minutes — do not boil',
              'The precipitate particles visibly coarsen and settle more readily',
              'The supernatant liquid should become clearer as particles grow larger',
            ],
          }
        },
        {
          id: 7,
          title: 'Test for Complete Precipitation',
          shortDescription: 'Add a few drops of AgNO3 to test for complete precipitation',
          handler: 'handleTestPrecipitation',
          infoContent: {
            title: 'Complete Precipitation',
            content: 'Adding excess reagent ensures all analyte is precipitated. If more precipitate forms, add more AgNO3 and test again.',
            whatHappens: 'A few drops of AgNO3 are added to the clear supernatant to check if more precipitate forms; if it does, additional AgNO3 is needed to ensure all Cl- has been precipitated.',
            observe: [
              'Allow the precipitate to settle and add a few drops of AgNO3 to the clear supernatant',
              'If no new cloudiness appears, precipitation is complete',
              'If more precipitate forms, add additional AgNO3 and test again',
            ],
          }
        },
        {
          id: 8,
          title: 'Pre-Weigh Filter Paper',
          shortDescription: 'Record mass of clean, dry filter paper',
          handler: 'handlePreWeighFilter',
          validation: {
            requires: ['precipitation.isComplete'],
          },
          infoContent: {
            title: 'Pre-Weighing the Filter',
            content: 'Record the mass of a clean, dry filter paper before filtration. This mass will be subtracted from the final (filter + precipitate) mass to determine the precipitate mass alone.',
            whatHappens: 'The filter paper is pre-weighed so that after drying, the mass of AgCl precipitate alone can be determined by subtraction.',
            observe: [
              'Use a clean, dry filter paper of the correct size for the Buchner funnel',
              'Record the filter paper mass to four decimal places',
              'Handle the filter paper by the edges to avoid adding finger oils',
            ],
          },
        },
        {
          id: 9,
          title: 'Vacuum Filtration',
          shortDescription: 'Filter the precipitate using vacuum filtration with Buchner funnel',
          handler: 'handleVacuumFilter',
        },
        {
          id: 10,
          title: 'Wash Precipitate',
          shortDescription: 'Wash with two or three 5 mL portions of water, then three 5 mL portions of acetone',
          handler: 'handleWashPrecipitate',
          infoContent: {
            title: 'Two-Step Washing',
            content: `Wash the precipitate in two steps:
1. **Distilled water** — wash with two or three **5 mL portions** to remove soluble impurities (excess ions)
2. **Acetone** — pour three **5 mL portions** through the filter to displace water and speed drying

**CAUTION:** Acetone is highly flammable! Keep away from open flames.`,
            whatHappens: 'Water washes remove soluble impurities (excess Ag+, NO3-), then acetone displaces the water from the precipitate, significantly reducing drying time in the oven.',
            observe: [
              'Wash with 2-3 portions of 5 mL distilled water first',
              'Follow with three 5 mL portions of acetone — keep away from flames',
              'The precipitate should appear white and dry more quickly after acetone washing',
            ],
          },
        },
        {
          id: 11,
          title: 'Dry to Constant Mass',
          shortDescription: 'Dry at ~100 °C in oven, weigh the precipitate/filter paper, calculate mass of AgCl',
          handler: 'handleDryPrecipitate',
          infoContent: {
            title: 'Constant Mass Procedure',
            content: `Place the filter paper on a numbered watch glass and dry in the oven at **~100 °C**. When it has dried, weigh the precipitate/filter paper and calculate the mass of AgCl.

Using your data, calculate the percentage of chloride in the original unknown sample.`,
            whatHappens: 'The filter paper with AgCl precipitate is dried in an oven at ~100 C until successive weighings give the same mass (constant mass), ensuring all moisture has been removed.',
            observe: [
              'Place the filter paper on a numbered watch glass for tracking',
              'Weigh after drying and check that the mass is constant (does not change on re-drying)',
              'Calculate mass of AgCl = (filter + precipitate mass) - (filter paper mass)',
            ],
          }
        },
        {
          id: 12,
          title: 'Calculate %Cl',
          shortDescription: 'Calculate the percent chloride using the gravimetric factor',
          handler: 'handleCalculateChloride',
          validation: {
            requires: ['precipitateMass']
          },
          infoContent: {
            title: 'Gravimetric Factor',
            content: 'g Cl in sample = (X g AgCl) × (35.45 g Cl / 143.32 g AgCl) = (X g AgCl) × (0.2473). The %Cl = (g of Cl in sample / g of unknown sample) × 100.',
            whatHappens: 'The gravimetric factor (0.2473) converts the mass of AgCl precipitate to the mass of chloride originally present in the unknown sample.',
            observe: [
              'g Cl = mass of AgCl x 0.2473 (ratio of molar masses: 35.45/143.32)',
              '%Cl = (g Cl / g unknown sample) x 100',
              'Perform the analysis in duplicate and compare results for reproducibility',
            ],
          }
        }
      ]
    },
    {
      id: 2,
      name: 'phosphorus',
      title: 'Part II: Phosphorus in Fertilizer',
      equipment: [
        'Balance',
        '250 mL beakers',
        'Filter funnel',
        'Buchner funnel (5 inch)',
        'Vacuum flask',
        'Filter paper (large, 5 inch)',
        'Drying oven (~100 °C)',
        'Glass stirring rod',
        'Rubber policeman',
        'Plastic wash bottle',
        'Watch glass',
        'Graduated cylinders'
      ],
      initialState: {
        fertilizerMass: null,
        precipitateMass: null,
        percentP: null,
        percentP2O5: null,
        balance: {
          reading: 0,
          isTared: false,
          isStable: true
        },
        precipitation: {
          isComplete: false,
          isStirring: false,
          precipitateAmount: 0
        },
        filtration: {
          vacuumOn: false,
          precipitateCollected: 0,
          washCount: 0
        },
        drying: {
          isComplete: false,
          massReadings: []
        }
      },
      steps: [
        {
          id: 1,
          title: 'Weigh Fertilizer',
          shortDescription: 'Weigh to the nearest hundredth gram around 1 g of soluble fertilizer',
          handler: 'handleWeighFertilizer',
          validation: {
            requires: ['balance.isTared'],
            condition: 'fertilizerMass >= 0.9 && fertilizerMass <= 1.1'
          }
        },
        {
          id: 2,
          title: 'Dissolve Fertilizer',
          shortDescription: 'Add 25-30 mL of deionized water and stir (some may not dissolve)',
          handler: 'handleDissolveFertilizer'
        },
        {
          id: 3,
          title: 'Filter Insolubles',
          shortDescription: 'Filter to remove insoluble materials from the fertilizer',
          handler: 'handleFilterInsolubles'
        },
        {
          id: 4,
          title: 'Add MgSO₄·7H₂O Solution',
          shortDescription: 'Add about 30 mL of 10% MgSO₄·7H₂O solution to the filtrate',
          handler: 'handleAddMgSO4',
          infoContent: {
            title: 'Magnesium Source',
            content: 'Add about **30 mL** of **10% MgSO₄·7H₂O** (magnesium sulfate heptahydrate) solution. The Mg²⁺ ions combine with ammonium and phosphate to form the insoluble precipitate MgNH₄PO₄·6H₂O.',
            whatHappens: 'Magnesium sulfate solution provides Mg2+ ions that will combine with ammonium and phosphate to form the insoluble precipitate magnesium ammonium phosphate hexahydrate.',
            observe: [
              'Add approximately 30 mL of the 10% MgSO4.7H2O solution',
              'The solution may become slightly cloudy as Mg2+ begins interacting with phosphate',
              'Stir to ensure thorough mixing before adding ammonia',
            ],
          }
        },
        {
          id: 5,
          title: 'Add Ammonia',
          shortDescription: 'Slowly add approximately 100 mL of 2 M NH₃ while stirring',
          handler: 'handleAddAmmonia',
          infoContent: {
            title: 'Precipitation Conditions',
            content: 'Slowly add approximately **100 mL of 2 M NH₃(aq)** while stirring. A **white precipitate** of MgNH₄PO₄·6H₂O will form. Allow the mixture to sit at room temperature for **15 minutes** to complete the precipitation.',
            chemicalEquation: 'Mg²⁺ + NH₄⁺ + PO₄³⁻ + 6H₂O → MgNH₄PO₄·6H₂O(s)',
            whatHappens: 'Ammonia provides both NH4+ ions and a basic pH, causing magnesium ammonium phosphate hexahydrate (MgNH4PO4.6H2O) to precipitate as a white crystalline solid.',
            observe: [
              'A white precipitate of MgNH4PO4.6H2O forms upon adding ammonia',
              'Add the ammonia slowly (~100 mL of 2 M) while stirring continuously',
              'Allow the mixture to sit for 15 minutes to complete precipitation',
            ],
          }
        },
        {
          id: 6,
          title: 'Stir Until Crystalline',
          shortDescription: 'Continue stirring until crystalline precipitate forms',
          handler: 'handleStirCrystallize',
          infoContent: {
            title: 'Crystal Formation',
            content: 'Stirring promotes the formation of well-defined crystals that are easier to filter and wash.',
            whatHappens: 'Continued stirring promotes the growth of larger, well-defined MgNH4PO4.6H2O crystals that are easier to filter and less likely to pass through the filter paper.',
            observe: [
              'The initially fine precipitate becomes more visibly crystalline with stirring',
              'Crystals appear white and granular as they grow in size',
              'Larger crystals settle faster and filter more efficiently',
            ],
          }
        },
        {
          id: 7,
          title: 'Allow to Settle',
          shortDescription: 'Allow mixture to sit at room temperature for 15 minutes to complete precipitation',
          handler: 'handleSettlePrecipitate',
          validation: {
            requires: ['precipitation.isComplete'],
          },
          infoContent: {
            title: 'Settling Time',
            content: 'Allow the mixture to sit at room temperature for **15 minutes** to complete the precipitation. This allows the crystals to grow larger and ensures more complete precipitation.',
            whatHappens: 'A 15-minute settling period allows the precipitation to reach completion and permits crystals to grow to a filterable size.',
            observe: [
              'Leave the beaker undisturbed for 15 minutes at room temperature',
              'The precipitate settles to the bottom, leaving clearer liquid above',
              'Larger crystals are visible after the settling period',
            ],
          },
        },
        {
          id: 8,
          title: 'Filter on Pre-Weighed Paper',
          shortDescription: 'Vacuum filter precipitate onto pre-weighed filter paper',
          handler: 'handleFilterPhosphate',
        },
        {
          id: 9,
          title: 'Wash Precipitate',
          shortDescription: 'Wash with two or three 5 mL portions of deionized water, then two 10 mL portions of 75% isopropyl alcohol',
          handler: 'handleWashPhosphate',
          infoContent: {
            title: 'Two-Step Washing (Part 2)',
            content: `Wash the precipitate with two or three **5 mL portions of deionized water**. Add each portion to the beaker in which you did the precipitation to transfer any remaining precipitate; then pour over the solid on the funnel.

Finally, pour two **10 mL portions of 75% isopropyl alcohol** through the filter paper.`,
            whatHappens: 'Water washes remove soluble impurities, and isopropyl alcohol displaces water from the precipitate to speed drying, similar to the acetone wash in Part I.',
            observe: [
              'Rinse the precipitation beaker with wash water to transfer all remaining precipitate to the funnel',
              'Wash with 2-3 portions of 5 mL deionised water, then 2 portions of 10 mL 75% isopropyl alcohol',
              'The isopropyl alcohol is flammable — keep away from heat sources',
            ],
          },
        },
        {
          id: 10,
          title: 'Dry to Constant Mass',
          shortDescription: 'Dry at ~100 °C in oven on numbered watch glass, weigh when dry',
          handler: 'handleDryPhosphate',
          infoContent: {
            title: 'Constant Mass',
            content: 'Remove the filter paper and place it on a numbered watch glass and let it dry in the oven at **~100 °C**. When dry, weigh the filter paper/precipitate. Record the mass and calculate the percentages of phosphorus and P₂O₅ in your sample.',
            whatHappens: 'The filter paper with MgNH4PO4.6H2O precipitate is dried at ~100 C until constant mass is achieved, ensuring accurate determination of the precipitate mass.',
            observe: [
              'Place on a numbered watch glass for oven drying at ~100 C',
              'Weigh after drying, then re-dry and re-weigh to confirm constant mass',
              'Calculate precipitate mass = (filter + precipitate) - (pre-weighed filter paper)',
            ],
          },
        },
        {
          id: 11,
          title: 'Calculate %P and %P₂O₅',
          shortDescription: 'Calculate phosphorus content using gravimetric factors',
          handler: 'handleCalculatePhosphorus',
          validation: {
            requires: ['precipitateMass']
          },
          infoContent: {
            title: 'Phosphorus Gravimetric Factors',
            content: 'GF for P = M(P)/M(MgNH₄PO₄·6H₂O) = 30.97/245.40. GF for P₂O₅ = M(P₂O₅)/(2 × M(MgNH₄PO₄·6H₂O)) = 141.92/(2 × 245.40). Remember to compare actual results to the expected results from the package.',
            whatHappens: 'Gravimetric factors convert the mass of MgNH4PO4.6H2O precipitate to the mass of phosphorus and P2O5 originally present in the fertilizer sample.',
            observe: [
              'Calculate %P using GF = 30.97/245.40 = 0.1262',
              'Calculate %P2O5 using GF = 141.92/(2 x 245.40) = 0.2892',
              'Compare your results to the guaranteed analysis on the fertilizer package',
            ],
          }
        }
      ]
    }
  ],
  quiz: {
    experimentId: 'exp7',
    title: 'Gravimetric Analysis Quiz',
    questions: [
      {
        id: 'exp7-q1',
        type: 'mcq' as const,
        question: 'What are the four basic steps of gravimetric analysis?',
        options: [
          { id: 'a', text: 'Dissolve, titrate, calculate, report' },
          { id: 'b', text: 'Precipitate, filter, dry to constant mass, weigh' },
          { id: 'c', text: 'Mix, heat, cool, measure' },
          { id: 'd', text: 'Prepare, react, extract, chromatograph' },
        ],
        correctOptionId: 'b',
        explanation: 'Gravimetric analysis involves precipitating the analyte, filtering it, drying to constant mass (repeated heating/cooling/weighing until mass is stable), and weighing.',
      },
      {
        id: 'exp7-q2',
        type: 'calculation' as const,
        question: 'Calculate the gravimetric factor for determining chloride from AgCl. (Atomic masses: Cl = 35.45, Ag = 107.87)',
        expectedValue: 0.2474,
        tolerance: 2,
        unit: '',
        explanation: 'GF = mass of Cl / mass of AgCl = 35.45 / (107.87 + 35.45) = 35.45 / 143.32 = 0.2474',
      },
      {
        id: 'exp7-q3',
        type: 'mcq' as const,
        question: 'How do you test for complete precipitation of chloride?',
        options: [
          { id: 'a', text: 'Add more sample and check if precipitate forms' },
          { id: 'b', text: 'Add a drop of AgNO₃ to the clear supernatant and check for turbidity' },
          { id: 'c', text: 'Measure the pH of the solution' },
          { id: 'd', text: 'Heat the solution and observe' },
        ],
        correctOptionId: 'b',
        explanation: 'After the precipitate settles, add a drop of AgNO₃ to the clear supernatant. If no turbidity appears, precipitation is complete. If turbidity occurs, more AgNO₃ is needed.',
      },
      {
        id: 'exp7-q4',
        type: 'short_answer' as const,
        question: 'Explain the concept of "constant mass" and why it is important in gravimetric analysis.',
        sampleAnswer: 'Constant mass means the precipitate is repeatedly heated, cooled in a desiccator, and weighed until successive weighings differ by no more than 0.5 mg. This ensures all moisture and volatile impurities have been removed, giving an accurate and reproducible mass of the pure precipitate for calculation.',
      },
      {
        id: 'exp7-q5',
        type: 'calculation' as const,
        question: 'If 0.4520 g of AgCl precipitate was obtained from 0.5000 g of unknown sample, what is the %Cl? (GF = 0.2474)',
        expectedValue: 22.37,
        tolerance: 3,
        unit: '%',
        explanation: '%Cl = (mass AgCl × GF / mass sample) × 100 = (0.4520 × 0.2474 / 0.5000) × 100 = 22.37%',
      },
    ],
  },
};

export default experiment7Config;
